import React, { createContext, useContext } from "react";

interface SessionContextValue {
  currentUser: server.dto.UserSmall;
  isLoggedIn: boolean;
  isInternal: boolean;
  isExternal: boolean;
  betaFeaturesEnabled: boolean;

  clientId: number;
  client: server.dto.ClientSmall | null;
  isPaid: boolean;
  isSystemOneAccount: boolean;
}

const defaultSessionContext: SessionContextValue = {
  currentUser: null,
  isLoggedIn: false,
  isInternal: true,
  isExternal: false,
  betaFeaturesEnabled: false,

  clientId: 0,
  client: null,
  isPaid: false,
  isSystemOneAccount: false,
};

export const SessionContext = createContext<SessionContextValue>(defaultSessionContext);

// This is the new session provider that will provide the current logged in environment instead of site data
//  Still remaining references that can be moved here:
//   *site.currentUser* - 16 (including this)
//   *site.currentEnvironment.client* - done
//   *site.currentEnvironment.type* - 2
export const SessionProvider = (props: React.PropsWithChildren) => {
  const hasCurrentEnvironment = site.currentEnvironment != null;
  const hasClient = hasCurrentEnvironment && site.currentEnvironment.client != null;

  return <SessionContext.Provider value={{
    currentUser: site.currentUser,
    isLoggedIn: site.currentUser != null,
    isInternal: hasCurrentEnvironment ? site.currentEnvironment.type === "internal" : true,
    isExternal: hasCurrentEnvironment ? site.currentEnvironment.type === "external" : false,
    betaFeaturesEnabled: hasCurrentEnvironment ? site.permissions.betaAccess : false,
    clientId: hasClient ? site.currentEnvironment.client.clientId : 0,
    client: hasClient ? site.currentEnvironment.client : null,
    isPaid: hasClient ? site.currentEnvironment.client.access === "activePaid" : false,
    isSystemOneAccount: hasClient ? site.currentEnvironment.client.clientId === 1 : false,
  }}>
    {props.children}
  </SessionContext.Provider>
};

export const useCurrentUser = () => useContext(SessionContext).currentUser;
export const useIsLoggedIn = () => useContext(SessionContext).isLoggedIn;
export const useIsInternal = () => useContext(SessionContext).isInternal;
export const useIsExternal = () => useContext(SessionContext).isExternal;
export const useBetaFeaturesEnabled = () => useContext(SessionContext).betaFeaturesEnabled;

export const useClientId = () => useContext(SessionContext).clientId;
export const useClient = () => useContext(SessionContext).client;
export const useIsPaid = () => useContext(SessionContext).isPaid;
export const useIsSystemOneAccount = () => useContext(SessionContext).isSystemOneAccount;